/**
 * AddressLabel Component
 *
 * Displays a property address formatted as a mailing label
 * Shows on hover over an address icon in property tables
 *
 * ⚠️ The lines are the LETTER'S OWN (utils/letterPreview.js, which asks the function the
 * mailing export prints with). Until Oct 2026 this preview was built by another function
 * (utils/addressFormatter.js), which does not read the Land Registry address a scraped
 * home's letter is printed from: it was titled "Mailing Address Preview" and was not the
 * mailing address. A record the letter cannot print at all says so here.
 */

import React, { useState } from "react";
import PropTypes from "prop-types";
import { letterLines } from "../../../utils/letterPreview";
import "./AddressLabel.scss";

export const CANNOT_PRINT = "A mailshot cannot be printed from this record as it is stored.";

// SVG Address Label Icon
export const AddressLabelIcon = ({ className = "", size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`address-label-icon ${className}`}
  >
    {/* Envelope/Label outline */}
    <rect
      x="2"
      y="5"
      width="20"
      height="14"
      rx="2"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
    />
    {/* Address lines */}
    <line
      x1="5"
      y1="9"
      x2="13"
      y2="9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <line
      x1="5"
      y1="12"
      x2="16"
      y2="12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <line
      x1="5"
      y1="15"
      x2="11"
      y2="15"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    {/* Stamp in corner */}
    <rect
      x="17"
      y="7"
      width="4"
      height="3"
      rx="0.5"
      stroke="currentColor"
      strokeWidth="1"
      fill="none"
    />
  </svg>
);

export const AddressLabel = ({ property }) => {
  const [showLabel, setShowLabel] = useState(false);
  const printed = letterLines(property);
  const cannotPrint = printed === null;
  const addressLines = cannotPrint ? [CANNOT_PRINT] : printed;

  if (addressLines.length === 0) {
    return null;
  }

  return (
    <div
      className="address-label-wrapper"
      data-letter={cannotPrint ? "cannot-print" : "printed"}
      onMouseEnter={() => setShowLabel(true)}
      onMouseLeave={() => setShowLabel(false)}
    >
      <AddressLabelIcon size={16} className={`address-label-trigger${cannotPrint ? " address-label-trigger--cannot-print" : ""}`} />

      {showLabel && (
        <div className="address-label-popup">
          <div className="address-label-content">
            {addressLines.map((line, index) => (
              <div key={index} className="address-label-line">
                {line}
              </div>
            ))}
          </div>
          <div className="address-label-footer">
            <small>Mailing Address Preview</small>
          </div>
        </div>
      )}
    </div>
  );
};

AddressLabel.propTypes = {
  property: PropTypes.shape({
    paon: PropTypes.string,
    saon: PropTypes.string,
    street: PropTypes.string,
    streetName: PropTypes.string,
    locality: PropTypes.string,
    town: PropTypes.string,
    district: PropTypes.string,
    county: PropTypes.string,
    postcode: PropTypes.string,
    buildingNumber: PropTypes.string,
    buildingName: PropTypes.string,
  }).isRequired,
};

AddressLabelIcon.propTypes = {
  className: PropTypes.string,
  size: PropTypes.number,
};
